/**
 * English — source of truth.
 *
 * Every other catalogue falls back to this file key by key, so this one must
 * stay complete. When you add a string here, run `npm run i18n:coverage` to
 * see which languages are now behind.
 *
 * Placeholders use {braces}: "© {year} Tamil Data" -> t('key', { year: 2026 })
 */
export default {
  // -------------------------------------------------------------------------
  // Language switcher
  // -------------------------------------------------------------------------
  lang: {
    label: "Language",
    choose: "Choose your language",
    // Shown under the globe on wide screens.
    current: "Language: {name}",
    diasporaNote:
      "Tamil is spoken across more than 60 countries. Pick the language you read most comfortably.",
    group: {
      primary: "Primary",
      europe: "Europe",
    },
    // Where each language serves the Tamil diaspora.
    region: {
      en: "Global · India, Sri Lanka, Singapore, Malaysia, UK, US, Canada, Australia",
      ta: "Tamil Nadu, Sri Lanka, Singapore, Malaysia",
      de: "Germany, Switzerland, Austria",
      fr: "France, Canada, Switzerland, Réunion, Mauritius",
      es: "Spain, Latin America",
      it: "Italy",
      nl: "Netherlands, Belgium",
      no: "Norway",
      da: "Denmark",
      sv: "Sweden",
    },
  },

  // -------------------------------------------------------------------------
  // Shared UI
  // -------------------------------------------------------------------------
  common: {
    siteName: "Tamil Data",
    organization: "Tamil Data Foundation",
    tagline: "Decentralizing the Legacy of Language",
    subTagline: "Decentralizing Digital Assets of the Tamil Language",
    downloadClient: "Download Client",
    login: "Login",
    joinUs: "Join Us",
    learnMore: "Learn more",
    readMore: "Read more",
    loading: "Loading",
    filter: "Filter",
    reset: "Reset",
    report: "Report",
    submit: "Submit",
    close: "Close",
    choose: "Choose...",
    email: "Email",
    phone: "Phone",
    completed: "Completed",
    upcoming: "Upcoming",

    // Shared by every application form. See WhatsAppApplicationForm.vue.
    sendViaWhatsApp: "Send via WhatsApp",
    formRequiredFields: "Please complete the highlighted fields.",
    formConsentRequired: "Please confirm you consent before submitting.",
    formPrivacyNote:
      "Your details are sent directly to the Foundation over WhatsApp. Nothing is stored on this website, and we do not pass your information to anyone else.",
  },

  // -------------------------------------------------------------------------
  // Navigation
  // -------------------------------------------------------------------------
  nav: {
    home: "Home",
    about: "About",
    initiatives: "Initiatives",
    decentralization: "Decentralization",
    researchFund: "Tamil Research Fund",
    rewardName: "Reward Tamil Name",
    fourthChild: "Fourth Child Support",
    archive: "Archive",
    // Team moved to the footer; the key is still used there.
    team: "Team",
    // Workshops now live in the sidebar of the decentralization page rather
    // than as a top-level nav item.
    workshop: "Workshop",
    contact: "Contact",
    faq: "FAQ",
    toggle: "Toggle navigation",
  },

  // -------------------------------------------------------------------------
  // Route metadata (document title + meta description)
  // -------------------------------------------------------------------------
  meta: {
    home: {
      title:
        "Tamil Data Foundation — Decentralizing Tamil Language Digital Assets",
      description:
        "Tamil Data Foundation uses blockchain and IPFS to decentralize the digital assets of Tamil, the world's oldest living classical language. Contribute, validate, and earn rewards.",
    },
    about: {
      title: "About the Foundation",
      description:
        "Why the digital heritage of Tamil — the oldest living classical language — belongs on a decentralized, community-owned network rather than in private silos.",
    },
    decentralization: {
      title: "Tamil Data Decentralization",
      description:
        "How Tamil Data works end to end: upload Tamil digital assets, community validation, blockchain rewards and merchant discounts, and permanent decentralized storage on IPFS.",
    },
    fund: {
      title: "Tamil Language Research Fund",
      description:
        "Grants for research into Sangam literature, archaeological excavations, Tamil-Brahmi epigraphy, palm-leaf manuscripts, and Tamil language technology. A written proposal is required.",
    },
    rewardName: {
      title: "Reward for a Tamil Name",
      description:
        "Give your child a good Tamil name. A free naming guide with over 40,000 Tamil names, plus a monetary reward for parents who choose a pure Tamil name for their child.",
    },
    fourthChild: {
      title: "Fourth Child Support",
      description:
        "A one-time payment to Tamil parents in Eelam on the birth of a fourth child. War cost our community an immeasurable number of lives; this initiative supports the families choosing to carry the next generation forward.",
    },
    archive: {
      title: "Tamil Digital Archive",
      description:
        "Browse Tamil digital assets pinned to IPFS, organized by a bilingual Tamil knowledge taxonomy covering literature, education, arts, law, religion, and social science.",
    },
    team: {
      title: "Team",
      description:
        "The people building the Tamil Data Foundation and the global community supporting it.",
    },
    workshop: {
      title: "Workshops & Events",
      description:
        "Community workshops and events run by the Tamil Data Foundation on digitization, validation, and decentralized storage of Tamil assets.",
    },
    joinUs: {
      title: "Join Us",
      description:
        "Take part in decentralizing Tamil digital heritage as a contributor, validator, node operator, or partner organization.",
    },
    faq: {
      title: "Frequently Asked Questions",
      description:
        "What Tamil Data is, how to contribute, which data types are accepted, how rewards are calculated and paid, and how contributions are protected.",
    },
    contact: {
      title: "Contact",
      description:
        "Reach the Tamil Data Foundation. Open to contributors, researchers, Tamil associations, sponsors, and partner organizations worldwide.",
    },
  },

  // -------------------------------------------------------------------------
  // Home page — Vue hero plus the static sections in public/index.html
  // -------------------------------------------------------------------------
  home: {
    hero: {
      // The multi-script wordmark is deliberately never translated.
      cta: "Elevating the world's oldest living classical language through blockchain technology",
    },
    whyUs: {
      title: "Why Decentralization?",
      body: "We are on a mission to empower Tamil language speakers and enthusiasts by creating a data ecosystem where contributors are rewarded for their efforts.",
      box1: {
        title: "Decentralized Systems",
        body: "Improved security, greater privacy, and enhanced governance with no single point of failure.",
      },
      box2: {
        title: "Merchant Rewards and Vouchers",
        body: "Enjoy exclusive discounts and vouchers from participating merchants.",
      },
      box3: {
        title: "Earn Blockchain Rewards",
        body: "Each contribution is recorded on the blockchain for transparency and immutability.",
      },
    },
    stats: {
      nodeOperators: "Node Operators",
      assets: "Decentralized Tamil Digital Assets",
      rewards: "Rewards Paid",
      merchants: "Participating Merchants",
    },
    supporters: {
      titleLead: "Supporting",
      titleHighlight: "Organizations",
    },
    testimonials: {
      titleLead: "Why Are They",
      titleHighlight: "Supporting Us?",
      quote1:
        "By backing this project, we aim to decentralize the digital assets of the world's oldest living classical language, safeguarding its legacy while promoting data ownership and privacy in the digital age.",
      role1: "Founder — Kenwyn Accounting",
      quote2:
        "We are committed to decentralizing the digital assets of the world's oldest living classical language, ensuring its enduring preservation and promoting a more inclusive and secure digital future for cultural heritage.",
      role2: "Founder — TrincoITHub.org",
    },
  },

  // -------------------------------------------------------------------------
  // Footer (static markup in public/index.html)
  // -------------------------------------------------------------------------
  footer: {
    address: "Address",
    contact: "Contact",
    openingHours: "Opening Hours",
    monSat: "Mon–Sat",
    sunday: "Sunday",
    closed: "Closed",
    followUs: "Follow Us",
    faq: "FAQ",
    joinUs: "Join Us",
    team: "Team",
    copyright:
      "© {year} TamilData.org is an initiative by the Tamil Data Foundation, supported by the global Tamil community.",
    developedBy: "Developed by",
  },

  // -------------------------------------------------------------------------
  // About
  // -------------------------------------------------------------------------
  about: {
    question: "What is data decentralization?",
    answer:
      "Rather than keeping digitized material on one person's computer or inside a single organization's systems, decentralization lets every Tamil language enthusiast hold a copy — so the archive survives independently of any single host.",
    platform:
      "Tamil Data provides a secure, transparent, and community-driven space for contributors to upload and validate valuable data. Participants earn rewards and receive exclusive discounts from our merchant partners.",
    ecosystemTitle: "A blockchain-based Tamil language ecosystem",
    ecosystemBody:
      "TAMIL Crypto is the first blockchain-based technology dedicated to preserving the world's oldest living classical language. It helps digitize and decentralize Tamil language assets.",
    rewardsBody:
      "It rewards content publishers for their work, and node operators and validators for powering the decentralized network.",
    zhaTitle: 'Why the "ழ" (ZHA) symbol?',
    zhaBody1:
      "Of the 247 letters in the Tamil alphabet, ZHA is the most distinctive, because of both its pronunciation and its usage.",
    zhaBody2:
      "ழ (ZHA) is the 30th letter of the Tamil alphabet. Two other letters carry a similar sound — LA and LLA — but ZHAGARAM stands apart for its unique articulation.",
  },

  // -------------------------------------------------------------------------
  // Decentralization initiative
  // -------------------------------------------------------------------------
  decentralization: {
    eyebrow: "Initiatives",
    title: "Tamil Data Decentralization",
    subtitle: "Decentralized Data Sharing",
    intro:
      "Through Tamil Data you can share the material you digitize with the whole community in a secure and fully transparent way, without it sitting on individual computers or in a single organization's systems. The process works in four steps:",
    steps: [
      {
        title: "Upload data",
        body: "Upload Tamil language material — books, articles, research data — using our Windows desktop client.",
      },
      {
        title: "Validate data",
        body: "Uploads are verified by the community, so only accurate, good-quality material is accepted.",
      },
      {
        title: "Earn rewards and discounts",
        body: "Contributions earn blockchain rewards, plus exclusive discounts from our merchant partners.",
      },
      {
        title: "Decentralized storage",
        body: "Your material is stored using decentralized technology, keeping it accessible to Tamil enthusiasts everywhere.",
      },
    ],
    closing:
      "Join this initiative and play your part in preserving the Tamil language and its culture.",
  },

  // -------------------------------------------------------------------------
  // Tamil Language Research Fund
  // -------------------------------------------------------------------------
  fund: {
    eyebrow: "Initiatives",
    title: "Tamil Language Research Fund",
    // Tamil rendering of the fund name, shown as a subtitle in non-Tamil
    // locales. Set to "" in the Tamil catalogue to avoid repeating the title.
    titleNative: "தமிழ் மொழி ஆய்வு நிதி",
    lead: "A community-funded grant that backs serious research into the Tamil language and the material record of Tamil civilisation — from Sangam literature to ongoing archaeological excavations. Every funded output is published openly and pinned to IPFS, so the findings stay accessible to everyone, permanently.",
    ctaSubmit: "Submit a Proposal",
    ctaExplore: "Explore Research Areas",

    notice: {
      title: "A written proposal is mandatory",
      body: "The fund does not disburse on informal requests. Every applicant — individual researcher, university department, or institution — must submit a written research proposal for review before any funding decision is made.",
    },

    areasTitle: "Research Areas",
    areasSubtitle: "What the fund is looking to support",
    areas: [
      {
        title: "Sangam Literature",
        native: "சங்க இலக்கியம்",
        description:
          "Textual scholarship on the earliest layer of Tamil literature and the classical grammatical tradition.",
        examples: [
          "Critical editions and variant readings of Ettuthogai and Pattupattu",
          "Concordances, lexicons, and annotated translations",
          "Tolkappiyam commentary studies",
          "Computational stylometry and relative dating",
        ],
      },
      {
        title: "Archaeological Excavations",
        native: "அகழ்வாராய்ச்சி",
        description:
          "Permitted fieldwork and post-excavation study that grounds Tamil history in material evidence.",
        examples: [
          "Keeladi, Adichanallur, Porunthal, Sivagalai and related sites",
          "Radiocarbon and AMS dating of stratified samples",
          "Iron age and megalithic burial studies",
          "Archaeobotany, metallurgy, and ceramic analysis",
        ],
      },
      {
        title: "Epigraphy & Tamil-Brahmi",
        native: "கல்வெட்டு மற்றும் தமிழி",
        description:
          "Reading, dating, and cataloguing inscriptions across stone, copper plate, and pottery.",
        examples: [
          "Tamil-Brahmi potsherd graffiti and cave inscriptions",
          "Temple and hero-stone inscription surveys",
          "Palaeographic sequencing of scripts",
          "3D scanning and photogrammetry of eroded surfaces",
        ],
      },
      {
        title: "Palm-Leaf Manuscripts",
        native: "ஓலைச்சுவடி",
        description:
          "Rescuing manuscript collections that are decaying in private and institutional custody.",
        examples: [
          "Cataloguing and conservation surveys",
          "High-resolution imaging and OCR for Tamil script",
          "Siddha medicine and astronomy manuscripts",
          "Provenance and scribal tradition studies",
        ],
      },
      {
        title: "Trade, Diaspora & History",
        native: "வணிகம், புலம்பெயர்வு, வரலாறு",
        description:
          "The reach of Tamil civilisation across the Indian Ocean and the modern diaspora.",
        examples: [
          "Indo-Roman and Southeast Asian maritime trade evidence",
          "Tamil settlements in Sri Lanka, Malaysia, and beyond",
          "Plantation-era migration archives and oral history",
          "Numismatics and trade-route mapping",
        ],
      },
      {
        title: "Tamil Language Technology",
        native: "தமிழ் மொழி தொழில்நுட்பம்",
        description:
          "Open corpora and models that make Tamil a first-class language in modern computing.",
        examples: [
          "OCR for classical print, palm-leaf, and inscriptions",
          "Speech corpora across regional dialects",
          "Open benchmarks and annotated NLP datasets",
          "Historical Tamil to modern Tamil normalisation",
        ],
      },
    ],

    eligibilityTitle: "Who Can Apply",
    eligibilitySubtitle: "Open to researchers anywhere in the world",
    eligibility: [
      "Independent scholars and doctoral or post-doctoral researchers",
      "University departments of Tamil, history, archaeology, linguistics, or computer science",
      "Museums, libraries, and archives holding Tamil manuscripts or artefacts",
      "Registered Tamil cultural and literary associations",
      "Interdisciplinary teams combining humanities and language technology",
    ],

    conditionsTitle: "Funding conditions",
    conditions: [
      "All outputs must be released under an open licence and pinned to IPFS through the Tamil Data network.",
      "Excavation and fieldwork proposals must include valid permits from the relevant state archaeology department or national authority. The fund does not support unauthorised digging.",
      "Primary data — transcriptions, photographs, measurements, recordings — must be deposited, not just the final paper.",
      "Milestone reports are required before each subsequent disbursement.",
      "Findings must be attributable and reproducible; the fund does not support speculative or unverifiable claims.",
    ],

    processTitle: "Proposal Process",
    processSubtitle: "From concept note to published findings",
    process: [
      {
        title: "Concept note",
        description:
          "Send a one to two page summary of the research question, why it matters, and the rough scale of funding required. This is a low-effort first filter.",
      },
      {
        title: "Full proposal",
        description:
          "If the concept fits the fund's scope, you are invited to submit the full proposal with methodology, timeline, itemised budget, and team credentials.",
      },
      {
        title: "Panel review",
        description:
          "An academic panel of Tamil scholars, archaeologists, and linguists assesses the proposal for rigour, feasibility, and originality.",
      },
      {
        title: "Community disclosure",
        description:
          "Shortlisted proposals and their budgets are published to the community. The fund is community-financed, so allocation decisions are made in the open.",
      },
      {
        title: "Grant agreement",
        description:
          "Funding is released against agreed milestones rather than as a single lump sum, with a short progress report at each stage.",
      },
      {
        title: "Open publication",
        description:
          "Findings, datasets, and imagery are pinned to IPFS under an open licence, permanently citable and free for anyone to build on.",
      },
    ],

    requirementsTitle: "What Your Proposal Must Contain",
    requirementsSubtitle: "Incomplete submissions are returned without review",
    requirements: [
      {
        title: "Research question and significance",
        description:
          "What exactly are you investigating, and what gap in current Tamil scholarship does it close?",
      },
      {
        title: "Methodology",
        description:
          "Sources, sites, sampling approach, analytical methods, and how conclusions will be validated.",
      },
      {
        title: "Timeline and milestones",
        description:
          "Phase-wise schedule with deliverables that can be independently checked at each milestone.",
      },
      {
        title: "Itemised budget",
        description:
          "Line-item costs with justification. State any co-funding or institutional support already secured.",
      },
      {
        title: "Team and credentials",
        description:
          "Investigators, institutional affiliation, relevant publications, and prior fieldwork experience.",
      },
      {
        title: "Permits and ethics",
        description:
          "Copies of excavation or archive access permissions, plus consent arrangements for any oral history or community participation.",
      },
      {
        title: "Outputs and open-access plan",
        description:
          "What will be produced — papers, datasets, scans, editions — and how each will be licensed and deposited to IPFS.",
      },
      {
        title: "Risks",
        description:
          "An honest account of what could derail the work, and the fallback plan if it does.",
      },
    ],

    submitTitle: "Submit Your Proposal",
    submitBody:
      'Send your concept note or full proposal as a PDF, with the subject line "Research Fund Proposal — [your title]".',
    submitNative:
      "உங்கள் ஆய்வு முன்மொழிவை PDF வடிவில் கீழ்க்காணும் மின்னஞ்சலுக்கு அனுப்பவும்.",
    reviewNote:
      "Proposals are reviewed on a rolling basis by an academic panel. Expect an acknowledgement within two weeks and a review outcome within eight weeks of a complete submission.",
  },

  // -------------------------------------------------------------------------
  // Join Us
  // -------------------------------------------------------------------------
  joinUs: {
    title:
      "Worldwide contribution is needed to make this Tamil digital decentralization project succeed.",
    intro:
      "Tamil Data seeks the collaboration of Tamil enthusiasts and organizations worldwide to preserve the Tamil language and its culture. Your contribution is vital to the success of this initiative.",
    freeAccountLabel: "Free account",
    freeAccountBody: "Sign up for free at {url} and join the project.",
    globalLabel: "Global contribution",
    globalBody:
      "This project depends on the cooperation of the Tamil community worldwide. Upload your material and help preserve the digital assets of the Tamil language.",
    associationsLabel: "Calling Tamil associations",
    associationsBody:
      "We invite Tamil associations around the world to partner with us on this global-scale project. Your organization's involvement will help make it a success.",
    closing: "Joining is completely free. Register for an account now: {url}",
  },

  // -------------------------------------------------------------------------
  // Workshops
  // -------------------------------------------------------------------------
  workshop: {
    // Sidebar heading on the decentralization page.
    sidebarTitle: "Workshops",
    sidebarSubtitle: "Free online sessions, open to everyone",
    cardTitle: "Decentralizing the Digital Assets of the Tamil Language",
    cardTitleNative: "தமிழ் மின்னியல் ஆவண பரவலாக்கம்",
    timeSouthAsia: "Sri Lanka / India",
    timeSoutheastAsia: "Singapore / Malaysia",
    joinMeet: "Join Google Meet",
    rewardNote: "Contribute and get rewarded in Tamil Crypto",
    noUpcoming: "No sessions are scheduled right now. Check back soon.",
    showPast: "Show past sessions ({count})",
    hidePast: "Hide past sessions",
  },

  // -------------------------------------------------------------------------
  // Shared disclosures shown on both funded initiatives.
  //
  // Kept in one place because the two schemes are funded and limited the same
  // way, and applicants deserve the same honest picture on either page.
  // -------------------------------------------------------------------------
  initiativeStatus: {
    title: "Where this stands today",
    subtitle: "Said plainly, so you know what to expect",
    items: [
      {
        label: "Funded by one person, for now",
        body: "This scheme is currently paid for out of one individual's own pocket. That has an honest consequence: verification and transfer can take longer than you would like, and there is a limit to how many families we can reach in any given month. We would rather say so upfront than leave you waiting without explanation.",
      },
      {
        label: "Building the network",
        body: "We are in conversation with other Tamil patriots who want to put this on a firmer footing. As more of them come on board, both the speed of verification and the scale of assistance improve.",
      },
      {
        label: "Open to Eelam first",
        body: "Applications are currently accepted from Tamil families living in Eelam. We are working with partners to extend the scheme to Tamil Nadu and to other countries where Tamil communities are under real financial strain.",
      },
      {
        label: "Where this is heading",
        body: "The direction of travel is Tamil Community Crowd Funding: donors connected directly to the families they support, with the Foundation verifying applications rather than sitting in the middle of the money. That direct connection is what will let this scale.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Fourth Child Support
  // -------------------------------------------------------------------------
  fourthChild: {
    eyebrow: "Initiatives",
    headlineNative:
      "இழந்த தலைமுறையை நினைவில் கொள்வோம். வரும் தலைமுறையை வளர்ப்போம்.",
    headline: "Remember the generation we lost. Raise the one to come.",
    lead: "War cost the Tamil people of Eelam an immeasurable number of lives. Whole families ended. Villages that once filled with children went quiet. A community carries that absence for generations, and it shows in more than grief — it shows in how few hands are left to carry the language, the land and the memory forward. This initiative stands with families who choose to welcome a fourth child, with a one-time payment when that child is born.",
    remembrance: "In memory of those who did not come home.",

    whyTitle: "Why this matters",
    why: [
      {
        title: "A people continues through its children",
        body: "Language, custom and memory are passed on in households, not in archives. A household needs children to pass them to.",
      },
      {
        title: "The loss was not evenly shared",
        body: "The families and districts that lost the most in the war are often the same ones now under the heaviest economic pressure. Recovery has not reached them equally.",
      },
      {
        title: "Cost is the obstacle, not willingness",
        body: "Many parents would welcome another child and stop at three because of what a fourth would mean for school fees, food and rent. That is a financial barrier, and a financial barrier can be lowered.",
      },
      {
        title: "An offer, never an expectation",
        body: "How many children to raise is a decision for parents and no one else. Our part is only to remove an obstacle for those who have already made that choice.",
      },
    ],

    supportTitle: "What the support is",
    supportBody:
      "A one-time payment to the parents on the birth of a fourth child, transferred to their bank account once the application has been verified. It is meant to take the edge off the first year. It does not pretend to cover the cost of raising a child, which no single payment could.",

    eligibilityTitle: "Who can apply",
    eligibility: [
      "Tamil parents living in Eelam. See the note on reach below.",
      "The application is for a fourth child, born to the same parents.",
      "Both parents' names and a contact number we can reach you on are needed for verification.",
      "One payment per family.",
    ],

    processTitle: "How it works",
    process: [
      "Submit the form below. It reaches us on WhatsApp.",
      "We confirm the details with you directly and verify the birth.",
      "Once verified, we ask for bank details privately — never through this form.",
      "The payment is transferred to the parents' account.",
    ],

    formTitle: "Apply for support",
    formSubtitle:
      "Submitting opens WhatsApp with your details filled in, ready to send.",
    form: {
      parentsName: "Parents' names",
      childName: "Child's name",
      birthDate: "Child's date of birth",
      country: "Country",
      district: "District",
      contactNumber: "Contact number",
      contactNumberPlaceholder: "With country code, e.g. +94 …",
      consent:
        "I am a parent or legal guardian of this child and I consent to these details being shared with the Tamil Data Foundation for this application.",
    },
    whatsappHeading: "Fourth Child Support — application",
  },

  // -------------------------------------------------------------------------
  // Reward for a Tamil Name
  // -------------------------------------------------------------------------
  rewardName: {
    eyebrow: "Initiatives",
    // The Tamil headline is the heart of this page and is shown in every
    // locale, with a translation underneath for non-Tamil readers.
    headlineNative:
      "நல்ல தமிழ்ப் பெயரைப் பிள்ளைக்குச் சூட்டுங்கள். நானொரு தமிழனென்று அடையாளம் காட்டுங்கள்!",
    headline:
      "Give your child a good Tamil name. Let them carry that identity.",
    lead: "A name is the first word a child answers to, and the first thing the world learns about them. Across the diaspora, Tamil families increasingly reach for borrowed or hybrid names — often simply because a good Tamil name was hard to find at the moment it was needed. This initiative removes that obstacle, and rewards the choice.",

    whyTitle: "Why it matters",
    why: [
      "A name in pure Tamil carries the language forward into a generation that may grow up far from Tamil Nadu or Eelam.",
      "Names are the most durable part of a language. Long after a household switches to another tongue at the dinner table, the names remain.",
      "Choosing Tamil is a quiet act of continuity — no ceremony, no expense, and it lasts a lifetime.",
    ],

    guideTitle: "The naming guide",
    guideBody:
      "We point you to a reference collection of more than 40,000 Tamil names, gathered and organised for exactly this purpose. Browse it, pick a name that fits your family, and use it with confidence.",
    guideCta: "Open the Tamil naming guide",
    guideCredit:
      "Guide hosted by tamilnation.org, compiled by Thangavelu. Tamil Data Foundation does not host or own this collection.",

    rewardTitle: "The reward",
    rewardBody:
      "Parents who name their child in pure Tamil and submit the details below receive a monetary reward, transferred to the parents' bank account once the application is verified.",
    rewardSteps: [
      "Choose a pure Tamil name, using the guide or your own knowledge.",
      "Submit the form below. It reaches us on WhatsApp.",
      "We verify the name and confirm the details with you directly.",
      "Once verified, we ask for bank details privately and transfer the reward.",
    ],
    // Deliberately explicit: people should not be sending account numbers
    // through a public form or an unsolicited message.
    bankNotice:
      "Never send bank details in this form or in your first message. We will ask for them privately, only after your application has been verified.",

    formTitle: "Apply for the reward",
    formSubtitle:
      "Submitting opens WhatsApp with your details filled in, ready to send.",
    form: {
      childName: "Child's name",
      childNamePlaceholder: "As registered, if already registered",
      tamilName: "Tamil name chosen",
      tamilNamePlaceholder: "In Tamil script if you can",
      parentsName: "Parents' names",
      country: "Country",
      district: "District",
      contactNumber: "Contact number",
      contactNumberPlaceholder: "With country code, e.g. +94 …",
      consent:
        "I am a parent or legal guardian of this child and I consent to these details being shared with the Tamil Data Foundation for this application.",
    },
    // First line of the generated WhatsApp message.
    whatsappHeading: "Reward for a Tamil Name — application",
  },

  // -------------------------------------------------------------------------
  // Team
  // -------------------------------------------------------------------------
  team: {
    founder: "Founder — TamilData.org",
    developer: "Developer",
  },

  // -------------------------------------------------------------------------
  // Archive
  // -------------------------------------------------------------------------
  archive: {
    title: "Decentralized Global Tamil Digital Archive",
    subtitle: "Decentralizing digital assets of the Tamil language",
    joinPrompt: "Help protect our classical language — join the project today:",
    joinLink: "Join now",
    selectMainCategory: "Select main category",
    selectSubCategory: "Select sub classification",
    approvedAssets: "Approved Assets",
    table: {
      validateDate: "Validate Date",
      node: "Node",
      fileId: "File ID",
      category: "Category",
      subCategory: "Sub Category",
      description: "Description",
      reason: "Reason",
    },
  },

  // -------------------------------------------------------------------------
  // FAQ
  // -------------------------------------------------------------------------
  faq: {
    title: "Frequently Asked Questions",
    items: [
      {
        question: "What is TamilData.org?",
        answer:
          "TamilData.org is a decentralized data platform powered by the blockchain, focused on the Tamil language. Users can upload and validate data to earn blockchain rewards and enjoy merchant benefits.",
      },
      {
        question: "How do I start using TamilData?",
        answer:
          "Download the TamilData client from our official website, create an account, and start uploading or validating data.",
      },
      {
        question: "What types of data can I contribute?",
        answer:
          "We accept Tamil language data, including text, audio, and other valuable digital content.",
      },
      {
        question: "How are rewards calculated?",
        answer:
          "Rewards are based on the quantity and quality of your contributions. Validating data also earns rewards.",
      },
      {
        question: "How do I receive my rewards?",
        answer:
          "Rewards are recorded on the blockchain and transferred to your wallet.",
      },
      {
        question: "How can I redeem merchant rewards and vouchers?",
        answer:
          "Our participating merchants provide exclusive discounts and vouchers for contributors. Use the Data-DEX platform to view and redeem these offers.",
      },
      {
        question: "Is there any cost to participate?",
        answer:
          "Data-DEX is free to download and use. However, you may need to manage your own wallet.",
      },
      {
        question: "How is my data protected?",
        answer:
          "Data-DEX uses blockchain technology to ensure that data transactions are secure, transparent, and tamper-proof.",
      },
      {
        question: "Who can I contact for support?",
        answer:
          "Please reach out to our support team through the contact form on our website or email us at support@tamildata.org.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Contact
  // -------------------------------------------------------------------------
  contact: {
    heading: "Every Tamil language lover has a role",
    subheading:
      "...and can make an impact on preserving Tamil assets for the next generations.",
    imageAlt: "Global Tamil community effort",
    card1: {
      title: "Contributors & Supporters",
      body: "Whether you're a developer, researcher, linguist, or simply passionate about Tamil — there's a place for you in this mission.",
    },
    card2: {
      title: "Organizations & Associations",
      body: "Tamil associations, cultural organizations, and tech communities worldwide are welcome to partner with us.",
    },
    card3: {
      title: "Sponsors & Partners",
      body: "Support the decentralization of Tamil digital heritage and be part of a global movement.",
    },
    reachOut: "Interested? Please reach out to us:",
  },
};
